import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { CaptureType } from '../../components/id-verfication/capture-type.enum';

import { MISNAP_CAPTURE_OPTIONS } from './misnap-option.config';
import { FrameCaptureResult } from './responses/frame-capture-result.model';
import { FrameProcessingFeedback } from './responses/frame-processing-feedback.model';
import { SdkError } from './responses/sdk-error.model';
import { GetSdkLicenseResponse } from './responses/get-sdk-license-response.model';

@Injectable({
  providedIn: 'root',
})
export class MisnapService {
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);
  private readonly http = inject(HttpClient);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _mitekScienceSDK: any = null;
  private license: string | null = null;

  private documentTypeMap: Record<CaptureType, string> = {
    [CaptureType.Selfie]: 'SELFIE',
    [CaptureType.IdFront]: 'DL_FRONT',
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private get sdk(): any {
    if (!this.isBrowser) return null;
    if (!this._mitekScienceSDK) {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      this._mitekScienceSDK = require('../../../../../assets/mitekSDK/mitek-science-sdk');
    }
    return this._mitekScienceSDK;
  }

  async getSdkLicense(): Promise<string> {
    if (!this.license) {
      const response = await firstValueFrom(
        this.http.get<GetSdkLicenseResponse>(
          '/api/id-verification/sdk-license',
        ),
      );
      this.license = response.sdkLicense;
    }

    return this.license;
  }

  registerFeedbackHandler(
    callback: (data: FrameProcessingFeedback) => void,
  ): void {
    if (!this.isBrowser) return;

    this.sdk.on('FRAME_PROCESSING_FEEDBACK', (data: unknown) => {
      callback(data as FrameProcessingFeedback);
    });
  }

  registerCaptureResultHandler(callback: (data: FrameCaptureResult) => void) {
    if (!this.isBrowser) return;

    this.sdk.on('FRAME_CAPTURE_RESULT', (data: unknown) => {
      callback(data as FrameCaptureResult);
    });
  }

  registerErrorHandler(callback: (data: SdkError) => void) {
    if (!this.isBrowser) return;

    this.sdk.on('SDK_ERROR', (data: unknown) => {
      callback(data as SdkError);
    });
  }

  async startAutoCapture(
    captureType: CaptureType,
    cameraContainerId = 'misnap-camera-container',
    videoContainerId = 'misnap-video',
  ): Promise<void> {
    if (!this.isBrowser) return;

    const options = {
      ...MISNAP_CAPTURE_OPTIONS,
      cameraContainerId,
      videoContainerId,
      license: await this.getSdkLicense(),
    };

    this.sdk.cmd('CAPTURE_AND_PROCESS_FRAME', {
      mode: 'AUTO_CAPTURE',
      documentType: this.documentTypeMap[captureType],
      mitekSDKPath: 'assets/mitekSDK/',
      options,
    });
  }

  stopSDK(): void {
    if (!this.isBrowser) return;
    this.sdk.cmd('SDK_STOP', {});
  }
}
